/**
 * Past / current / future classification of a tenant's booking, used by the
 * portfolio-wide global search to tag tenant suggestions.
 */

/** Where a booking sits on the tenant's timeline, relative to today. */
export type TenantTimeline = "past" | "current" | "future";

/**
 * Booking statuses that never made someone a tenant of the room: an
 * unsubmitted draft, a request still awaiting approval, a cancellation, or an
 * application the archive-on-sign triggers closed because a competitor signed
 * (they set `status = 'archived'`). Every other status is searchable as a
 * past, current or future tenant.
 */
export const NON_TENANT_BOOKING_STATUSES = [
  "draft",
  "pending",
  "cancelled",
  "archived",
] as const;

/** Statuses that end a tenancy whatever its dates say (an early move-out keeps
 *  its original end_date). */
const ENDED_BOOKING_STATUSES = new Set(["moved_out", "terminated"]);

const NON_TENANT = new Set<string>(NON_TENANT_BOOKING_STATUSES);

/**
 * Classify a booking as past / current / future, by the Tenants page rule
 * (AdminTenants): dates decide — start after today is future, end before
 * today is past, otherwise current; a booking with no dates counts as future
 * until it has started. Ended statuses are always past.
 *
 * Returns `null` for a booking that is not a tenancy at all: a
 * `NON_TENANT_BOOKING_STATUSES` status, or an archived one. `admin_archived_at`
 * is the archive flag (see `src/lib/tenant-live-booking.ts`): the manual
 * Archive toggle stamps it and leaves `status` as it was, so a hand-archived
 * leftover application can still read `confirmed` with future dates. An
 * archived booking therefore only counts once it is past — a former tenant an
 * admin filed away — never as a current or future one.
 *
 * Dates are compared as calendar days, not as `Date` instants. The Tenants
 * page parses them with `new Date("YYYY-MM-DD")` (UTC midnight), which in
 * Germany puts a booking starting today at 01:00/02:00 local — after local
 * midnight — so it reads as future on move-in day. Same rule, without that
 * off-by-one.
 *
 * @param today Local calendar date as `YYYY-MM-DD`; booking dates are plain
 *   `YYYY-MM-DD` strings, so string comparison is date comparison.
 */
export function tenantTimeline(
  b: {
    status: string | null;
    start_date: string | null;
    end_date: string | null;
    admin_archived_at?: string | null;
  },
  today: string
): TenantTimeline | null {
  if (b.status && NON_TENANT.has(b.status)) return null;
  const timeline = datedTimeline(b, today);
  if (b.admin_archived_at && timeline !== "past") return null;
  return timeline;
}

function datedTimeline(
  b: {
    status: string | null;
    start_date: string | null;
    end_date: string | null;
  },
  today: string
): TenantTimeline {
  if (b.status && ENDED_BOOKING_STATUSES.has(b.status)) return "past";
  const start = b.start_date?.slice(0, 10) ?? null;
  const end = b.end_date?.slice(0, 10) ?? null;
  if (start && start > today) return "future";
  if (end && end < today) return "past";
  if (!start) return "future";
  return "current";
}

/** Today's local calendar date as `YYYY-MM-DD`. */
export function localToday(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
